
import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';

import schoolLogo from '../assets/images/airforce-logo.png';

import resultPDF from "../assets/pdf/result.pdf";
import AcademicPlannerPDF from "../assets/pdf/Academic-Planner.pdf";
import ActivityList from "../assets/pdf/Activity-List.pdf";
import TCForm from "../assets/pdf/tc-form.pdf";
import MemberList from "../assets/pdf/SMC-MEMBER-LIST-2026-27.pdf";

import NewsFlash from './NewsFlash';
import Header from './Header';

const Navbar = () => {

    const [menuOpen, setMenuOpen] = useState(false);
    const [navFixed, setNavFixed] = useState(false);
    const navRef = useRef(null);
    const navTopRef = useRef(0);
    const [navHeight, setNavHeight] = useState(0);
    const location = useLocation();

    const closeMenu = () => setMenuOpen(false);

    useEffect(() => {
        closeMenu();
    }, [location.pathname]);

    // Keep the main navigation fixed after it reaches the top of the viewport.
    useEffect(() => {
        const updateNavPosition = () => {
            if (!navRef.current) return;

            if (!navFixed) {
                navTopRef.current = navRef.current.getBoundingClientRect().top + window.scrollY;
                setNavHeight(navRef.current.offsetHeight);
            }

            setNavFixed(window.scrollY >= navTopRef.current);
        };

        updateNavPosition();
        window.addEventListener('scroll', updateNavPosition, { passive: true });
        window.addEventListener('resize', updateNavPosition);

        return () => {
            window.removeEventListener('scroll', updateNavPosition);
            window.removeEventListener('resize', updateNavPosition);
        };
    }, [navFixed]);

    // Dynamic active route checking
    const isActive = (paths) => {
        return paths.includes(location.pathname) ? 'active' : '';
    };

    const isDropdownActive = (paths) => {
        return paths.includes(location.pathname) ? 'active' : '';
    };

    return (
        <>
            {/* TOP HEADER */}
            <div className="school-top-header">
                <Header />
            </div>

            {/* MAIN NAVBAR */}
            <>
                {navFixed && <div aria-hidden="true" style={{ height: navHeight }} />}
                <nav
                    ref={navRef}
                    className={`navbar navbar-expand-xl main-navbar ${navFixed ? 'main-navbar-fixed' : ''}`}
                    style={navFixed ? {
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        width: '100%',
                        zIndex: 1050,
                        boxShadow: '0 4px 18px rgba(0,0,0,0.12)'
                    } : { position: 'relative', zIndex: 1050 }}
                >
                <div className="container">

                    {/* LOGO */}
                    <Link
                        className="navbar-brand"
                        to="/"
                        onClick={closeMenu}
                    >
                        <div className="school-brand-wrapper">
                            <div className="school-logo-badge">
                                <img
                                    src={schoolLogo}
                                    alt="Air Force School Logo"
                                    className="school-logo-img"
                                />
                            </div>
                        </div>
                    </Link>

                    {/* MOBILE TOGGLE */}
                    <button
                        className="navbar-toggler"
                        type="button"
                        onClick={() => setMenuOpen(!menuOpen)}
                        aria-expanded={menuOpen}
                        aria-label="Toggle navigation"
                    >
                        <i className={`bi ${menuOpen ? 'bi-x-lg' : 'bi-list'}`}></i>
                    </button>

                    {/* NAVIGATION LINKS */}
                    <div className={`collapse navbar-collapse ${menuOpen ? 'show' : ''}`}>

                        <ul className="navbar-nav ms-auto align-items-xl-center">

                            {/* HOME */}
                            <li className="nav-item">
                                <Link
                                    className={`nav-link ${isActive(['/'])}`}
                                    to="/"
                                    onClick={closeMenu}
                                >
                                    Home
                                </Link>
                            </li>

                            {/* ABOUT US */}
                            <li className={`nav-item dropdown ${isDropdownActive(['/about', '/history', '/infrastructure'])}`}>
                                <a
                                    className="nav-link dropdown-toggle"
                                    href="#about-menu"
                                    role="button"
                                    data-bs-toggle="dropdown"
                                    aria-expanded="false"
                                >
                                    About Us
                                </a>

                                <ul className="dropdown-menu">
                                    <li>
                                        <Link className={`dropdown-item ${isActive(['/about'])}`} to="/about" onClick={closeMenu}>
                                            About School
                                        </Link>
                                    </li>

                                    <li>
                                        <Link className={`dropdown-item ${isActive(['/history'])}`} to="/history" onClick={closeMenu}>
                                            History
                                        </Link>
                                    </li>

                                    <li>
                                        <Link className={`dropdown-item ${isActive(['/infrastructure'])}`} to="/infrastructure" onClick={closeMenu}>
                                            Infrastructure
                                        </Link>
                                    </li>
                                </ul>
                            </li>

                            {/* ACADEMICS */}
                            <li className={`nav-item dropdown ${isDropdownActive(['/school-management', '/teacher-list', '/courses'])}`}>
                                <a
                                    className="nav-link dropdown-toggle"
                                    href="#academics-menu"
                                    role="button"
                                    data-bs-toggle="dropdown"
                                    aria-expanded="false"
                                >
                                    Academics
                                </a>

                                <ul className="dropdown-menu">
                                    <li>
                                        <Link className={`dropdown-item ${isActive(['/school-management'])}`} to="/school-management" onClick={closeMenu}>
                                            School Management
                                        </Link>
                                    </li>

                                    <li>
                                        <Link className={`dropdown-item ${isActive(['/teacher-list'])}`} to="/teacher-list" onClick={closeMenu}>
                                            Teacher List
                                        </Link>
                                    </li>

                                    <li>
                                        <Link className={`dropdown-item ${isActive(['/courses'])}`} to="/courses" onClick={closeMenu}>
                                            Courses
                                        </Link>
                                    </li>

                                    <li>
                                        <a className="dropdown-item" href={AcademicPlannerPDF} target="_blank" rel="noopener noreferrer">
                                            Academic Planner
                                        </a>
                                    </li>

                                    <li>
                                        <a className="dropdown-item" href={MemberList} target="_blank" rel="noopener noreferrer">
                                            SMC Member List
                                        </a>
                                    </li>
                                </ul>
                            </li>

                            {/* ADMISSION */}
                            <li className="nav-item dropdown">
                                <a
                                    className="nav-link dropdown-toggle"
                                    href="#admission-menu"
                                    role="button"
                                    data-bs-toggle="dropdown"
                                    aria-expanded="false"
                                >
                                    Admission
                                </a>

                                <ul className="dropdown-menu">
                                    <li>
                                        <Link className="dropdown-item" to="/admission-form" onClick={closeMenu}>
                                            Admission Form
                                        </Link>
                                    </li>

                                    <li>
                                        <a className="dropdown-item" href={resultPDF} target="_blank" rel="noopener noreferrer">
                                            Result Last Year
                                        </a>
                                    </li>

                                    <li>
                                        <a className="dropdown-item" href="/#calendarEventsSection" onClick={closeMenu}>
                                            Annual Report
                                        </a>
                                    </li>
                                </ul>
                            </li>

                            {/* NOTICE BOARD */}
                            <li className="nav-item dropdown">
                                <a
                                    className="nav-link dropdown-toggle"
                                    href="#notice-menu"
                                    role="button"
                                    data-bs-toggle="dropdown"
                                    aria-expanded="false"
                                >
                                    Notice Board
                                </a>

                                <ul className="dropdown-menu">
                                    <li>
                                        <a className="dropdown-item" href="/#whyChooseSection" onClick={closeMenu}>
                                            Admission Notice
                                        </a>
                                    </li>

                                    <li>
                                        <a className="dropdown-item" href="/#calendarEventsSection" onClick={closeMenu}>
                                            Circulars
                                        </a>
                                    </li>

                                    <li>
                                        <a className="dropdown-item" href={TCForm} target="_blank" rel="noopener noreferrer">
                                            TC Form
                                        </a>
                                    </li>
                                </ul>
                            </li>

                            {/* ACTIVITY */}
                            <li className="nav-item">
                                <a
                                    className="nav-link"
                                    href={ActivityList}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    Activity
                                </a>
                            </li>

                            {/* GALLERY */}
                            <li className="nav-item">
                                <Link
                                    className={`nav-link ${isActive(['/gallery'])}`}
                                    to="/gallery"
                                    onClick={closeMenu}
                                >
                                    Gallery
                                </Link>
                            </li>

                        </ul>

                        {/* CONTACT BUTTON */}
                        <div className="ms-xl-3 nav-contact-wrap mt-0">
                            <Link
                                to="/contact"
                                className={`btn btn-nav-admission ${isActive(['/contact'])}`}
                                onClick={closeMenu}
                            >
                                Contact Us <i className="bi bi-arrow-up-right"></i>
                            </Link>
                        </div>

                    </div>
                </div>
                </nav>
            </>

            {/* NEWS FLASH */}
            <NewsFlash />
        </>
    );
};

export default Navbar;
